import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames/bind";
import React, { forwardRef, useState } from "react";
import { TextDataDisplay } from "../DataDisplay/TextDataDisplay";
import { Skeleton } from "../Skeleton";
import styles from "./Chip.module.scss";
import type { ChipProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Used to display distinct, complete data, relative to a larger component or
 * higher-level containing concept
 */
const Chip = forwardRef<HTMLDivElement & HTMLButtonElement, ChipProps>(
  (
    {
      text,
      imageUrl,
      tooltipText,
      slotLeft,
      slotRight,
      isInteractive = false,
      isLoading = false,
      isBusy = false,
      colorVariant = "light",
      shapeVariant = "rounded",
      interactiveClassName,
      className,
      ...restProps
    }: ChipProps,
    ref
  ) => {
    const [isPressed, setIsPressed] = useState<boolean>(false);

    const chipClassName = cx(
      "chip",
      colorVariant && `is-color-${colorVariant}`,
      shapeVariant && `is-shape-${shapeVariant}`,
      imageUrl && "has-image",
      className
    );

    if (isInteractive) {
      const chipButton = (
        <div
          role="button"
          tabIndex={0}
          className={cx(
            "chip-button",
            colorVariant && `is-color-${colorVariant}`,
            shapeVariant && `is-shape-${shapeVariant}`,
            interactiveClassName
          )}
          aria-pressed={isPressed}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          {...restProps}
        >
          <div className={chipClassName}>
            {imageUrl ? (
              <img
                src={imageUrl}
                className={cx("image")}
                alt={text}
              />
            ) : (
              <>
                {slotLeft ? slotLeft() : null}
                {text && (
                  <TextDataDisplay
                    value={text}
                    clamp={1}
                  />
                )}
                {slotRight ? slotRight() : null}
              </>
            )}
          </div>
        </div>
      );

      return tooltipText ? (
        <Tooltip.Provider>
          <Tooltip.Root>
            <Skeleton
              isLoading={isLoading}
              isBusy={isBusy}
              className={cx("is-loading")}
            >
              <Tooltip.Trigger asChild>{chipButton}</Tooltip.Trigger>
            </Skeleton>
            <Tooltip.Portal>
              <Tooltip.Content
                className={cx("tooltip-content")}
                sideOffset={4}
                side="left"
              >
                {tooltipText}
                <Tooltip.Arrow className={cx("tooltip-arrow")} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        chipButton
      );
    }

    const chipEl = (
      <div
        ref={ref}
        className={chipClassName}
        tabIndex={0}
        {...restProps}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            className={cx("image")}
          />
        ) : (
          <>
            {slotLeft ? slotLeft() : null}
            {text && (
              <TextDataDisplay
                value={text}
                clamp={1}
              />
            )}
            {slotRight ? slotRight() : null}
          </>
        )}
      </div>
    );

    return tooltipText ? (
      <Tooltip.Provider>
        <Tooltip.Root>
          <Skeleton
            isLoading={isLoading}
            isBusy={isBusy}
            className={cx("is-loading")}
          >
            <Tooltip.Trigger asChild>{chipEl}</Tooltip.Trigger>
          </Skeleton>
          <Tooltip.Portal>
            <Tooltip.Content
              className={cx("tooltip-content")}
              sideOffset={4}
              side="bottom"
            >
              {tooltipText}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    ) : (
      chipEl
    );
  }
);

Chip.displayName = "Chip";

export default Chip;
